import React, { useState, useEffect, useRef } from 'react';
import { ChevronRight, LogOut } from 'lucide-react';

interface UserProfileProps {
    userEmail?: string;
    primaryColor?: string;
    onSignOut: () => void;
}

const UserProfile: React.FC<UserProfileProps> = ({
    userEmail,
    primaryColor,
    onSignOut
}) => {
    const [showSignOut, setShowSignOut] = useState(false);
    const userMenuRef = useRef<HTMLDivElement>(null);

    const handleUserClick = () => {
        setShowSignOut(!showSignOut);
    };

    const handleSignOut = () => {
        onSignOut();
        setShowSignOut(false);
    };

    // Get user initials from email
    const getUserInitials = (email: string) => {
        const name = email.split('@')[0];
        return name.substring(0, 2).toUpperCase();
    };

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
                setShowSignOut(false);
            }
        };

        if (showSignOut) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showSignOut]);

    return (
        <div className="border-t border-slate-200 pt-4 mt-auto" ref={userMenuRef}>
            <div className="space-y-2">
                <button
                    onClick={handleUserClick}
                    className="w-full flex items-center px-3 py-2 rounded-lg text-left hover:bg-slate-50 transition-colors"
                >
                    <div
                        className="w-8 h-8 rounded-full flex items-center justify-center mr-3"
                        style={{
                            backgroundColor: primaryColor || '#3B82F6',
                            background: primaryColor ? `linear-gradient(135deg, ${primaryColor}, ${primaryColor})` : 'linear-gradient(135deg, #3B82F6, #3B82F6)'
                        }}
                    >
                        <span className="text-xs font-medium text-white">
                            {userEmail ? getUserInitials(userEmail) : 'U'}
                        </span>
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-700 truncate">
                            {userEmail || 'User'}
                        </div>
                    </div>
                    <ChevronRight
                        className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${
                            showSignOut ? 'rotate-90' : ''
                        }`}
                    />
                </button>

                {showSignOut && (
                    <button
                        onClick={handleSignOut}
                        className="w-full flex items-center px-3 py-2 rounded-lg text-left hover:bg-red-50 text-red-600 transition-colors"
                    >
                        <LogOut className="w-4 h-4 mr-3" />
                        <span className="text-sm font-medium">Sign out</span>
                    </button>
                )}
            </div>
        </div>
    );
};

export default UserProfile;
